import * as actions_invoices from 'actions/invoices-actions';
import * as actions_quickbooks from 'actions/quickbooks-actions';
import * as actions_users from 'actions/users-actions';
import * as toolbox from 'components/common/toolbox';
import ActivityLog from 'components/activity-log/activity-log';
import Authorize from 'components/common/authorize';
import EllipsisDropdown from 'components/common/ellipsis-dropdown';
import QRCode from "react-qr-code";
import React, { useEffect, useState } from 'react';
import SendEmailForm from 'components/send-email-form/send-email-form';
import SpeedDialButton from 'components/common/speed-dial-button'; 
import StatusSelect from 'components/common/status-select';
import { APPS, ADDRESSES, CUSTOMERS, INVOICES, LOG, PERMISSIONS, SERVICE_ITEMS } from 'components/constants';
import { CloseX, Ibox, ModalAlert, Select, Spinner, ModalForm } from 'enspire-manager-framework';
import { confirmDelete, checkPopupBlocker } from 'components/common/toolbox';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';

const _ = require('lodash');
const moment = require('moment');

const InvoiceDashboard = (props) => {
    
    /* Hooks ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------*/
    
    const seg = 5;
    const params = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const routeLocation = useLocation();

    const company = useSelector(store => store.company.company);
    const customer = useSelector(store => store.profiles.profile);
    const features = useSelector((store) => store.settings?.settings?.features)?.[(params.appId == APPS.DISPATCH.id) ? 'dispatch' : 'assets'];
    const invoices = useSelector(store => store.invoices);
    const quickbooksSettings = useSelector((store) => store.quickbooks.quickbooks);
    const selectedCustomerId = useSelector((store) => store.assets.selected_customer_id);
    const service_request = useSelector((store) => store.serviceRequests.service_request);
    const settings = useSelector(store => store.settings);
    const user = useSelector((store) => store.users.user);
    const user_permissions = useSelector((store) => store.users.user_permissions);

    const synced = !!quickbooksSettings?.tokens?.access_token;
    const [showActivityModal, setShowActivityModal] = useState(false);

    const [highlight, setHighlight] = useState({});
    const [hasType, setHasType] = useState({});
    const [rendering, setRendering] = useState(false);

    const invoice = invoices.invoice;
    const permitted = user_permissions?.INVOICES_EDIT;

    // Permissions ----------------------------------------
    
    const isClosedOut = [INVOICES.PAID.id, INVOICES.VOID.id].includes(invoice?.statusId);
    const canReceivePayment = !isClosedOut && invoice?.statusId != INVOICES.PAID.id;
    const canClose = !isClosedOut && user_permissions[PERMISSIONS.SERVICE_REQUESTS_CLOSE.key];
    const canViewMyInvoice = (service_request?.techIds?.includes(user?.profileId) || service_request?.techIds?.includes(user?.email));
    const canEditInvoice = (!isClosedOut && (
        user_permissions[PERMISSIONS.INVOICES_EDIT.key] || 
        (canViewMyInvoice && user_permissions[PERMISSIONS.INVENTORY_CREATE.key])
    ));
        
    /* Effects ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------*/

    // Subscribe to active invoice
    useEffect(() => {
        dispatch(actions_invoices.subInvoice(params.handle, params.invoice_id));

        return () => {
            let unsubscribe = invoices.invoice_unsubscribe;
            if (typeof unsubscribe === 'function') unsubscribe();
        };
    }, [params.invoice_id]);

    useEffect(() => {
        Object.values(SERVICE_ITEMS).forEach((type) => {
            setHasType(Object.assign(hasType, { [type.id]: (!!_.find(invoice?.serviceItems, (i) => i.type == type.id)) }));
        });
    }, [invoice]);

    /* Handlers ------------------------------------------------------------------------------------------------------------------------------------*/

    const handleSpeedDial = (value, action) => {
        if (value == 'email') handleAction('email');
        if (value == 'print') handleAction('print');
        if (value == 'payment') openReceivePayment();
    }
    const handleAction = (action) => {
        if (action == 'print') {
            createInvoicePdf(null);
        } else if (action == 'email') {
            let location = toolbox.modifyPath(routeLocation.pathname, seg, 'send-email/0', seg+8);
            history.push({ pathname: location });
        }
    };

    /* Actions ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------*/

    const sendEmail = (email) => {
        createInvoicePdf(email);
    };
    const confirmDelete = () => {
        ModalAlert({
            title: 'Are you sure?',
            text: "This Invoice will be permanently deleted and associated Work Orders will be unlinked and made available for new Invoices.",
            icon: 'warning',
            show_cancel: true,
            confirm_color: '#8FBC8B',
            confirm_text: 'Yes, delete it!',
            callback_success: () => handleDelete(invoice),
        });
    };

    const handleDelete = () => {
        dispatch(actions_invoices.deleteInvoice(params.handle, invoice, () => {
            let location = toolbox.modifyPath(routeLocation.pathname, seg+2, `service_requests/${invoice.requestId}/invoices`, seg+4);
            props.history.replace({ pathname: location });
        }));
    };
    // const openHelpPanel = () => {
    //     if (window.flutter) {
    //         window.flutter.callHandler('helpPanel', '/servicing-assets.md/work-orders#add-a-locale-dropdown'); 
    //     } else {
    //         dispatch(actions_admin.showHelp(true, '/servicing-assets.md/work-orders#add-a-locale-dropdown'));
    //     }
	// }
    const openReceivePayment = () => {
        let location = toolbox.modifyPath(props.location.pathname, seg, 'payment-form/0');
        history.push({ pathname: location });
    };
    const openPaymentLink = () => {
        window.open(invoice.paymentLink, '_blank');
    };
    const createInvoicePdf = (email) => {
        var update = {};

        if (parseInt(invoice.statusId) < parseInt(INVOICES.SENT.id)) {
            if (!email) {
                ModalAlert({
                    title: 'Change Status?',
                    text: "Do you want to change status to Sent?",
                    icon: 'question',
                    confirm_color: '#8FBC8B',
                    confirm_text: 'Yes',
                    deny_text: 'No, keep as Draft',
                    deny_color: '#cccccc',
                    show_deny: true,
                    callback_success: () => { 
                        update.statusId = INVOICES.SENT.id;
                        continueCreate(); 
                    },
                    callback_denied: () => continueCreate()
                });
            } else {
                update.statusId = INVOICES.SENT.id;
                continueCreate(); 
            }
        } else continueCreate();

        function continueCreate() {
            if (email) history.goBack();
            setRendering(true);

            dispatch(actions_invoices.createInvoicePdf({
                handle: params.handle,
                shadeColor: '#116a75',
                profileId: (params.appId == APPS.DISPATCH.id) ? params.customer_id : selectedCustomerId,
                invoiceId: params.invoice_id,
                emails: (!email) ? null : (email?.to?.length) ? email.to.map((to) => to.value) : [], //email.to,
                subject: (!email) ? null : email.subject,
                body: (!email) ? null : email.body.replace(/(?:\r\n|\r|\n)/g, '<br>'),
                from: (company.email) ? company.companyName + ' <' + company.email + '>' : 'Mobile Track Systems <no-reply@mobiletrack.systems>',
            }, (result) => {

                dispatch(actions_invoices.updateInvoice(params.handle, params.invoice_id, update, null, true));
                dispatch(actions_quickbooks.updateQuickbooks({ handle: params.handle, type: 'invoice' }, quickbooksSettings));
                setRendering(false);

                if (result) {
                    if (!email) {
                        window.open(result.data.downloadUrl, '_blank');

                        if (!user.dismissed?.checkPopupBlocker) {
                            checkPopupBlocker((isDismissed) => {
                                if (isDismissed) dispatch(actions_users.dismissNotice(params.handle, user.email, 'checkPopupBlocker'));
                            }, () => {});
                        }

                    } else {
                        window.toastr.success('This Invoice has been generated and emailed to ' + email.to.map((to) => to.value).join(', '), 'Invoice Emailed!');
                    }
                } else {
                    window.toastr.error('Error generating Invoice', 'Error');
                }
            }));
        }
    };

    /* Constants ------------------------------------------------------------------------------------------------------------------------------------*/

    const activeTaxRates = settings.settings?.invoices?.tax_rates?.filter(rate => !rate.hidden) ?? [];

    if (invoice) {

        var totalPayments = (invoice.payments?.reduce((total, b) => total + parseFloat(b.amount), 0.00) ?? 0.00);

        /*------------------------------------
                CUSTOMER INFO
        -------------------------------------*/

        const billing_address = _.find(customer?.addresses, { addressTypeId: ADDRESSES.BILLING.id });
        const service_address = _.find(customer?.addresses, { addressTypeId: ADDRESSES.SERVICE.id });
        const bill_to = (billing_address) ? billing_address : service_address;
        var billing_info = toolbox.format_address(bill_to?.addressLine1, bill_to?.addressLine2, bill_to?.city, bill_to?.state, bill_to?.zip, null, null, true);
        var service_info = toolbox.format_address(service_address?.addressLine1, service_address?.addressLine2, service_address?.city, service_address?.state, service_address?.zip, null, null, true);
    }

    return (

        <>
            {invoice
                ?   <Authorize feature={'generate_invoices'} perm={ PERMISSIONS.INVOICES_VIEW } or={ canViewMyInvoice } show403>
                        <Ibox className="mt-3 mb-3" title={'INVOICE #' + invoice.id} no_fade={false}>
                            <CloseX onClick={() => { props.history.goBack(); }} style={{ right: '30px', marginTop: '-58px' }} />

                            <div className="d-flex justify-content-between flex-wrap">
                                {parseInt(invoice.statusId) == parseInt(INVOICES.PAID.id) && 
                                    <div style={{ position: 'absolute', zIndex: 101, left: '38%', top: 10 }}>
                                        <img src={'images/paid-stamp.png'} style={{ width: '125px', height: 'auto', }} />
                                    </div>
                                }
                                {parseInt(invoice.statusId) == parseInt(INVOICES.PARTIAL.id) && 
                                    <div style={{ position: 'absolute', zIndex: 101, left: '38%', top: 10 }}>
                                        <img src={'images/partial-payment-stamp.png'} style={{ width: '125px', height: 'auto', }} />
                                    </div>
                                }
                                <div className="mb-3">
                                    <h1 className="title">INVOICE</h1>

                                    {/* PAYMENT LINK */}
                                    { invoice.paymentLink && ![INVOICES.PAID.id, INVOICES.VOID.id].includes(invoice.statusId) &&
                                        <div className="d-flex align-items-center" style={{ cursor: 'pointer' }}>
                                            <div className="ml-3" role="button" onClick={ openPaymentLink }>
                                                <p className="m-0">Payment Link</p>
                                                <QRCode size="100" value={ invoice.paymentLink } />
                                            </div>
                                            <div className="btn btn-primary btn-sm mt-3 ml-4" onClick={ openPaymentLink }>
                                                <i className="fa-regular fa-credit-card mr-2"></i> Open Payment Link
                                            </div>
                                        </div>
                                    }
                                </div>
                                <div className="d-flex justify-content-end mr-4 mb-3" >
                                    <div className="text-right mr-3">
                                        <div><span className="r-titles">INVOICE&nbsp;#</span></div>
                                        <div><span className="r-titles">DATE:</span></div>
                                        <div><span className="r-titles">PAID:</span></div>
                                        <div><span className="r-titles">BALANCE:</span></div>
                                    </div>
                                    <div>
                                        <div><span className="r-titles-sub">{invoice.id}</span></div>
                                        <div><span className="r-titles-sub">{moment(invoice?.invoiceDate?.toDate()).format('MM/DD/YYYY')}<br /></span></div>
                                        <div><span className="r-titles-sub">{toolbox.formatNumber(totalPayments, 2, true)}<br /></span></div>
                                        <div><span className="r-titles-sub">{toolbox.formatNumber(invoice.totals?.grandTotal - totalPayments, 2, true)}<br /></span></div>
                                    </div>

                                </div>
                            </div>
                            
                            <div className="d-flex align-items-center position-relative">
                                <StatusSelect
                                    selectedStatusId={invoice.statusId}
                                    statusTable={'INVOICES'}
                                    statusTypes={Object.values(INVOICES)}
                                    static={true}
                                />
                                {invoice?.quickbooksId && 
                                    <img className='ml-2' src={'images/QuickBooks-Logo.png'} style={{ width: '30px', height: 'auto', }} />
                                }
                                <EllipsisDropdown actions={[
                                    ...(user_permissions[PERMISSIONS.INVOICES_DELETE.key]) ? [{ label: 'Delete Invoice', onClick: () => confirmDelete() }] : [],
                                    ...(user_permissions[PERMISSIONS.ACTIVITY_LOGS_VIEW.key]) ? [{ label: 'Activity Log', onClick: () => setShowActivityModal(true) }] : [],
                                ]} />
                            </div>

                            {/* <i className="fas fa-info-circle fa-lg text-info ml-3" onClick={openHelpPanel} role="button"></i> */}

                            { rendering &&
                                <button className="btn btn-info btn-sm ml-4">Rendering
                                    <span className="spinner-border spinner-border-sm ml-2" role="status" aria-hidden="true"></span>
                                </button>
                            }

                            <div className="row mt-2">&nbsp;</div>
                            <div className="row mb-3">
                                <div className="col-6">
                                    {!!billing_info.length &&
                                        <>
                                            <div className="title-bar">BILL TO: </div>
                                            <div className="p-2">
                                                <div className="font-bold">{ customer?.displayName }</div>
                                                <div>{billing_info}</div>
                                            </div>
                                        </>
                                    }
                                </div>
                                <div className="col-6">
                                    {!!service_info.length &&
                                        <>
                                            <div className="title-bar">SERVICE TO:</div>
                                            <div className="p-2">
                                                <div className="font-bold">{ customer?.displayName }</div>
                                                <div>{service_info}</div>
                                            </div>
                                        </>
                                    }
                                </div>
                            </div>
                        </Ibox>

                        {/* <button className="btn btn-warning btn-sm ml-2 mb-5" disable={!user_permissions?.INVOICES_EDIT} onClick={ confirmDelete }>Delete Invoice</button> */}

                        {/* { true && !admin.bottom_sheet_show && params.form == 'form' && segments < 12 && */}
                            <SpeedDialButton 
                                columnRef={ props.columnRef }
                                onClick={handleSpeedDial}
                                actions={[
                                    ...(true) ? [{
                                        name: 'Email to Customer', 
                                        value: 'email', 
                                        className: '', 
                                        icon: <i className="fa-solid fa-paper-plane-top fa-xl"></i> 
                                    }] : [],
                                    ...(true) ? [{
                                        name: 'Print', 
                                        value: 'print', 
                                        className: '', 
                                        icon: <i className="fas fa-print fa-xl"></i> 
                                    }] : [],
                                    ...(features['manage_payments'] && canReceivePayment) ? [{
                                        name: 'Receive Payment', 
                                        value: 'payment', 
                                        className: '', 
                                        icon: <i className="fa-regular fa-credit-card fa-xl"></i> 
                                    }] : [],
                                ]}
                            />
                        {/* } */}

                        {showActivityModal && (
                            <ModalForm
                                modal_header="Invoice Activity"
                                cancel_button_title="Close"
                                cancel_button_callback={() => setShowActivityModal(false)}
                                hide_submit={ true }
                                no_padding={ true }
                                size="md"
                            >
                                <ActivityLog
                                    id="invoice_id"
                                    prefix="INVOICE"
                                    singular="invoice"
                                    type={`${LOG.INVOICE.key}`}
                                    store="invoices"
                                />
                            </ModalForm>
                        )}

                    </Authorize>
                : <Spinner center />
            }

            {/* Integrate Routes under this component -------------------------------------------------- */}

            { params.form == 'send-email' &&
                <SendEmailForm
                    contacts={customer.contacts}
                    submit_callback={sendEmail}
                    type={'Invoice'}
                    id={params.invoice_id}
                />
            }
        </>
    );
};

export default InvoiceDashboard;